import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, Button, PasswordInput, Stack } from '@mantine/core';
import { IconArrowLeft, IconLink } from '@tabler/icons-react';
import { useAuth } from '@actschurch/ccms-sdk';
import { Callout, ErrorState, LoadingState, PageIntro } from '@/components/ui';
import { takePlanningCentreRedirect } from '@/lib/planningCentre';
import { wakeSync } from '@/lib/syncQueue';

type Phase = 'exchanging' | 'linkRequired' | 'redirecting' | 'failed';

/**
 * Planning Center OAuth callback (same as Carpool and the Resource Centre). Exchanges code + state;
 * on success continues to where the person was heading (usually the procedure they scanned). If an
 * account with the same email already exists, the person confirms its password once to link the two.
 */
export default function PlanningCentreCallbackPage() {
  const navigate = useNavigate();
  const { planningCentre } = useAuth();
  const ran = useRef(false);
  const [destination] = useState(() => takePlanningCentreRedirect());

  const [phase, setPhase] = useState<Phase>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke: the code can only be exchanged once
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errParam = params.get('error');
    if (errParam) {
      setError(errParam === 'access_denied' ? 'You cancelled the Planning Center sign-in.' : "Planning Center couldn't sign you in. Please try again.");
      setPhase('failed');
      return;
    }
    if (!code || !state) {
      setError('This sign-in link is incomplete. Please start again.');
      setPhase('failed');
      return;
    }
    (async () => {
      try {
        const result = await planningCentre.callback(code, state);
        if (result.status === 'LoggedIn') {
          setPhase('redirecting');
          wakeSync();
          navigate(destination, { replace: true });
        } else {
          setLinkToken(result.linkToken);
          setLinkEmail(result.email);
          setPhase('linkRequired');
        }
      } catch {
        setError("We couldn't finish signing you in. Please try again.");
        setPhase('failed');
      }
    })();
  }, [planningCentre, navigate, destination]);

  const onConfirmLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkToken) return;
    setConfirming(true);
    setError(null);
    try {
      await planningCentre.confirmLink(linkToken, password);
      wakeSync();
      navigate(destination, { replace: true });
    } catch {
      setError("That password doesn't match this account. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const backToSignIn = () => navigate(`/auth/login?redirect=${encodeURIComponent(destination)}`, { replace: true });

  if (phase === 'exchanging' || phase === 'redirecting') {
    return (
      <div className="proc-main">
        <div style={{ paddingTop: 48 }}>
          <LoadingState variant="spinner" label="Signing you in with Planning Center" />
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="proc-main">
        <PageIntro title="We couldn't sign you in" />
        <ErrorState
          title="Sign-in didn't finish"
          message={error ?? undefined}
          secondaryAction={<Button fullWidth onClick={backToSignIn}>Back to sign in</Button>}
        />
      </div>
    );
  }

  return (
    <div className="proc-main">
      <PageIntro
        title="Link your accounts"
        lede={`You already have an Acts Church account for ${linkEmail ?? 'this email'}. Enter its password once to link it to Planning Center. After that, Planning Center is all you need.`}
      />
      <div className="proc-card" style={{ padding: 18 }}>
        <form onSubmit={onConfirmLink}>
          <Stack gap="md">
            {error && <Callout tone="bad" live>{error}</Callout>}
            <PasswordInput label="Your Acts Church password" required value={password} onChange={(e) => setPassword(e.currentTarget.value)} autoComplete="current-password" autoFocus error={!!error || undefined} />
            <Button type="submit" loading={confirming} leftSection={<IconLink size={20} aria-hidden="true" />} fullWidth>Link and continue</Button>
            <Anchor href="https://ccms.actschurch.co.za/auth/forgot-password" target="_blank" rel="noopener noreferrer" ta="center" style={{ display: 'block', padding: '12px 0' }}>
              Forgot your password?
            </Anchor>
            <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={18} aria-hidden="true" />} onClick={backToSignIn} fullWidth>Back to sign in</Button>
          </Stack>
        </form>
      </div>
    </div>
  );
}
